import { formatGrams, formatKcal } from "@/lib/formatters";
import { Card } from "./ui/card";
import { RecipeCalculations } from "@/lib/types";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type NutritionAmount = {
  key: string;
  grams: number;
};

function NutritionRow({
  amounts,
  proportion,
  label,
  unit,
  indent,
}: {
  amounts: NutritionAmount[];
  proportion: number;
  label: ReactNode;
  unit: "g" | "kcal";
  indent?: boolean;
}) {
  return (
    <tr>
      <td
        className={cn(
          "bg-card sticky left-0 px-4 py-1 md:px-6 md:py-2",
          !indent && "font-medium",
          indent && "pl-8 md:pl-12",
        )}
      >
        {label}
      </td>
      {amounts.map((amount) => (
        <td
          className="px-4 py-1 text-right whitespace-nowrap md:px-6 md:py-2"
          key={amount.key}
        >
          {unit === "g"
            ? formatGrams(amount.grams * proportion)
            : formatKcal(amount.grams * proportion)}
        </td>
      ))}
    </tr>
  );
}

export function Nutrition({
  calculations,
  hideHeader,
  includeFullRecipeAmounts = true,
}: {
  calculations: RecipeCalculations;
  hideHeader?: boolean;
  includeFullRecipeAmounts?: boolean;
}) {
  const amounts = includeFullRecipeAmounts
    ? [
        { key: "100g", grams: 100 },
        { key: "recipe", grams: calculations.totalWeight },
      ]
    : [{ key: "100g", grams: 100 }];

  return (
    <Card className="overflow-x-auto md:max-w-max">
      <table className="w-full min-w-max table-auto text-sm">
        <thead>
          {!hideHeader && (
            <tr className="border-b-2">
              <th className="bg-card sticky left-0"></th>
              {includeFullRecipeAmounts && (
                <th className="px-4 py-2 text-right font-medium whitespace-nowrap md:px-6 md:py-3">
                  per {formatGrams(100)}
                </th>
              )}
              <th className="px-4 py-2 text-right font-medium whitespace-nowrap md:px-6 md:py-3">
                {includeFullRecipeAmounts
                  ? "Full recipe"
                  : `per ${formatGrams(100)}`}
              </th>
            </tr>
          )}
        </thead>
        <tbody className="divide-y">
          <NutritionRow
            amounts={amounts}
            proportion={
              calculations.totalWeight
                ? calculations.totalEnergyKcal / calculations.totalWeight
                : 0
            }
            label="Calories"
            unit="kcal"
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.fatPercentage}
            label="Total fat"
            unit="g"
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.saturatedFatPercentage}
            label="Saturated fat"
            unit="g"
            indent
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.transFatPercentage}
            label={
              <>
                <i>Trans</i> fat
              </>
            }
            unit="g"
            indent
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.carbohydratePercentage}
            label="Carbohydrates"
            unit="g"
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.sugarPercentage}
            label="Sugars"
            unit="g"
            indent
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.fiberPercentage}
            label="Fiber"
            unit="g"
            indent
          />
          <NutritionRow
            amounts={amounts}
            proportion={calculations.proteinPercentage}
            label="Protein"
            unit="g"
          />
        </tbody>
      </table>
    </Card>
  );
}
